// Every API request goes through fetchWithLogging: a time limit (kast-common's
// fetchWithTimeout), and an entry in the diagnostics API log that bug reports carry.
import { recordApiCall, redactString, redactUrl } from "@jkast90/kast-common/diagnostics";
import { fetchWithTimeout, type FetchWithTimeoutOptions } from "@jkast90/kast-common/fetch";

/** What a timed-out request says. Shown to the player as is. */
export const TIMEOUT_MESSAGE = "The server didn't answer in time. Check your connection and try again.";

/** Milliseconds before the request is cancelled, or null for no limit. Defaults to DEFAULT_TIMEOUT_MS (8 s). */
export type FetchOptions = Pick<FetchWithTimeoutOptions, "timeoutMs">;

/**
 * fetch, recorded in the API log (method, redacted URL, status, time; never
 * a header or a body), with a time limit. The limit also covers reading the
 * body, since the signal stays attached to the response. A timeout rejects
 * with RequestTimeoutError; the caller's own abort rejects as fetch would.
 */
export async function fetchWithLogging(
  input: RequestInfo | URL,
  init?: RequestInit,
  { timeoutMs }: FetchOptions = {},
): Promise<Response> {
  const url = typeof input === "string" ? input : input instanceof URL ? input.toString() : input.url;
  const method = (init?.method || "GET").toUpperCase();
  const start = Date.now();
  const startTime = performance.now();

  try {
    const response = await fetchWithTimeout(input, init, { timeoutMs, message: TIMEOUT_MESSAGE });
    recordApiCall({
      timestamp: start,
      method,
      path: redactUrl(url),
      status: response.status,
      durationMs: Math.round(performance.now() - startTime),
      error: response.ok ? null : `${response.status}`,
    });
    return response;
  } catch (thrown) {
    recordApiCall({
      timestamp: start,
      method,
      path: redactUrl(url),
      status: null,
      durationMs: Math.round(performance.now() - startTime),
      error: redactString(thrown instanceof Error ? thrown.message : String(thrown)),
    });
    throw thrown;
  }
}
